import React, { useEffect, useState, useCallback } from "react";
import Menu from "./Menu/Menu";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import toast from "react-hot-toast";
import Loading from "../../../Utils/Loading/Loading";
import useSound from "use-sound";
import ErrorSoundFile from "../../../Sound/Error.wav";
import NotFound from "../../../Utils/NotFound/NotFound";
import TotalCard from "./TotalCard/TotalCard";
import TradeCard from "./TradeCard";
import moment from "moment";
import Filter from "./Filter/Filter";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const fmt = require("indian-number-format");

const backend = await new Backend().init();

export default function Trade({ user_id }) {
  const userType = Number(localStorage.getItem("user_type") || 7);

  const [playError] = useSound(ErrorSoundFile);
  const windowSize = useSize();

  // ---------------------------------------------------
  // STATES
  // ---------------------------------------------------
  const [trade, setTrade] = useState([]);
  const [tradeSearch, setTradeSearch] = useState([]);

  const [commission, setCommission] = useState(0);
  const [booked, setBooked] = useState(0);
  const [nseCash, setNseCash] = useState(0);
  const [rent, setRent] = useState(0);
  const [tradeDelete, setTradeDelete] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isFetchedOnce, setIsFetchedOnce] = useState(false);

  const [selectedTrades, setSelectedTrades] = useState([]);
  const [tpassword, setTpassword] = useState("");

  const [start, setStart] = useState(
    moment().startOf("week").format("YYYY-MM-DD 00:00:00"),
  );
  const [end, setEnd] = useState(
    moment().endOf("week").format("YYYY-MM-DD 23:59:59"),
  );

  // ---------------------------------------------------
  // HANDLE API RESPONSE
  // ---------------------------------------------------
  const handleTradeResponse = useCallback(
    (r) => {
      if (!r) return;

      if (r.error === "False") {
        setTrade(r.order || []);
        setTradeSearch(r.order || []);
        setCommission(r.commission || 0);
        setBooked(r.booked || 0);
        setNseCash(r.nse_cash || 0);
        setRent(r.rent || 0);
        setTradeDelete(r.trade_delete || false);
      } else {
        playError();
        toast.error(r.message || "Something went wrong");
        if (r.logout === 1 && window.LogOut) window.LogOut();
      }
    },
    [playError],
  );

  // ---------------------------------------------------
  // BUILD PAYLOAD (IMPORTANT — FIXED)
  // ---------------------------------------------------
  const buildPayload = useCallback(
    (override = {}) => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      user_id: user_id,

      start: override.start || start,
      end: override.end || end,
    }),
    [user_id, start, end],
  );

  // ---------------------------------------------------
  // FETCH TRADES
  // ---------------------------------------------------
  const fetchTrade = useCallback(
    async (override = {}) => {
      setLoading(true);
      try {
        const payload = buildPayload(override);
        const r = await backend.trade(payload);
        handleTradeResponse(r);
      } catch (err) {
        playError();
        toast.error("Network error while loading trades");
      } finally {
        setLoading(false);
        setIsFetchedOnce(true);
      }
    },
    [buildPayload, handleTradeResponse, playError],
  );

  const handleBulkDelete = async (tp) => {
    if (selectedTrades.length === 0) {
      toast.error("Please select at least one trade");
      return;
    }

    try {
      const payload = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        delete_select: selectedTrades,
        tpassword: tp,
        type: "TRADE",
        admin: "1",
      };

      const r = await backend.delete(payload);

      if (r.error === "False") {
        toast.success(r.message);
        setSelectedTrades([]);
        fetchTrade();
      } else {
        toast.error(r.message);
      }
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // ---------------------------------------------------
  // INITIAL LOAD
  // ---------------------------------------------------
  useEffect(() => {
    fetchTrade();
  }, [fetchTrade]);

  // ---------------------------------------------------
  // FILTER HANDLER (FIXED)
  // ---------------------------------------------------
  const handleFilter = useCallback(
    ({ start, end }) => {
      setStart(start);
      setEnd(end);
      fetchTrade({ start, end }); // APPLY NEW DATES NOW ✔
    },
    [fetchTrade],
  );

  // ---------------------------------------------------
  // SEARCH
  // ---------------------------------------------------
  const search = useCallback(
    (query) => {
      const q = query.trim().toLowerCase();
      setTrade(
        q.length > 1
          ? tradeSearch.filter((t) =>
            t.symbol?.toLowerCase().includes(q.toLowerCase()),
          )
          : tradeSearch,
      );
    },
    [tradeSearch],
  );

  // ---------------------------------------------------
  // SORT HANDLER
  // ---------------------------------------------------
  const handleSort = useCallback((sorted) => setTrade(sorted), []);

  const exchangeMap = {
    nse: "NSE",
    mcx: "MCX",
    fo: "NSE-OPTIONS",
    mcx_options: "MCX-OPTIONS",
    nse_cash: "NSE-EQUITY",
    comex_future: "COMEX",
    crypto_future: "CRYPTO",
    forex_future: "FOREX",
    us_stock: "US-STOCK",
    sgx: "SGX",
    index: "OTHERS",
    dgcx: "DGCX",
    ncdex: "NCDEX",
  };

  // 📥 Lazy-load + optimized PDF generation
  const downloadUserPDF = useCallback(async () => {
    if (!trade.length) return toast.error("No data available to export");

    try {
      // ✅ Correct dynamic import for pdfmake
      const pdfMake = (await import("pdfmake/build/pdfmake")).default;
      const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;

      // ✅ Set virtual fonts
      pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs;

      const body = [
        [
          { text: "EXCH", style: "tableHeader" },
          userType === 1 ? { text: "SUPER", style: "tableHeader" } : null,
          userType === 1 || userType === 2
            ? { text: "MASTER", style: "tableHeader" }
            : null,
          { text: "USER", style: "tableHeader" },
          { text: "SYMBOL", style: "tableHeader" },
          { text: "EXPIRY", style: "tableHeader" },
          { text: "NET QTY", style: "tableHeader" },
          { text: "OPEN", style: "tableHeader" },
          { text: "RATE", style: "tableHeader" },
          { text: "B/S", style: "tableHeader" },
          { text: "BALANCE", style: "tableHeader" },
          { text: "VOLUME", style: "tableHeader" },
          { text: "TYPE", style: "tableHeader" },
          { text: "COMM", style: "tableHeader" },
          { text: "P/L", style: "tableHeader" },
          { text: "OTHER", style: "tableHeader" },
          { text: "DATE", style: "tableHeader" },
          { text: "TIME", style: "tableHeader" },
        ].filter(Boolean),
        ...trade.map((i) =>
          [
            { text: exchangeMap[i.script_type] },
            userType === 1 ? { text: i.super_username } : null,
            userType === 1 || userType === 2
              ? { text: i.master_username }
              : null,
            { text: i.username },
            { text: i.symbol },
            { text: moment(i.expiry_date).format("DD-MMM") },
            { text: i.qty },
            { text: i.standing_rate },
            { text: i.rate },
            {
              text: i.buy_sell === 0 ? "BUY" : "SELL",
              color: i.buy_sell === 0 ? "#399918" : "#FF0000",
            },
            { text: i.new_deposit },
            { text: i.amount },
            { text: i.type },
            {
              text: fmt.format(i.brokerage_user_amt),
              color: "#FF0000",
            },
            {
              text: fmt.format(i.profit_loss),
              color: i.profit_loss > 0 ? "#399918" : "#FF0000",
            },
            { text: i.other || "-" },
            {
              text: i.date_created
                ? moment(i.date_created).format("DD-MM-YYYY HH:mm:ss")
                : "-",
            },
            {
              text: i.trade_duration_minutes || "-",
            },
          ].filter(Boolean),
        ),
      ];

      // ✅ Create and trigger download
      pdfMake
        .createPdf({
          pageSize: "A3",
          pageOrientation: "landscape",
          pageMargins: [40, 60, 40, 60],
          // 👇 Add this
          defaultStyle: {
            fontSize: 9,
          },
          content: [
            {
              text: `${window.APP_NAME} USER TRADE REPORT`,
              style: "header",
              alignment: "center",
            },
            {
              text: `Generated on: ${moment().format("DD-MMM-YYYY HH:mm")}`,
              style: "subHeader",
            },
            {
              table: {
                headerRows: 1,
                widths: Array(body[0].length).fill("auto"),
                body,
              },
              layout: "lightHorizontalLines",
            },
          ],
          styles: {
            header: { fontSize: 18, bold: true },
            subHeader: {
              fontSize: 10,
              color: "#64748B",
              margin: [0, 0, 0, 10],
            },
            tableHeader: {
              bold: true,
              fontSize: 11,
              color: "#FFFFFF",
              fillColor: "#1E293B",
              alignment: "center",
            },
          },
          footer: (current, total) => ({
            text: `Page ${current} of ${total}`,
            alignment: "center",
            fontSize: 9,
          }),
        })
        .download(`${moment().format("DD-MMM")}-User-Trade-Report.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF. Check console for details.");
    }
  }, [trade, userType]);

  // 📊 Optimized Excel generation
  const downloadExcel = useCallback(() => {
    if (!trade.length) return toast.error("No data available");

    const data = trade.map((i) => ({
      Exch: exchangeMap[i.script_type],
      ...(userType === 1 && { Super: i.super_username }),
      ...((userType === 1 || userType === 2) && { Master: i.master_username }),
      User: i.username,
      Symbol: i.symbol,
      expiry: moment(i.expiry_date).format("DD-MMM"),
      NetQty: i.qty,
      OPEN: i.standing_rate,
      rate: i.rate,
      "B/S": i.buy_sell === 0 ? "BUY" : "SELL",
      balance: i.new_deposit,
      Volume: i.amount,
      type: i.type,
      Com: i.brokerage_user_amt,
      "P/L": i.profit_loss,
      Other: i.other || "-",
      Created: moment(i.date_created).format("DD-MM-YYYY HH:mm:ss") || "-",
      Time: i.trade_duration_minutes || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      `User-${moment().format("DD-MMM-YYYY")}.xlsx`,
    );
  }, [trade, userType]);

  // ---------------------------------------------------
  // RENDER
  // ---------------------------------------------------

  const toggleSelect = useCallback((id) => {
    setSelectedTrades((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const getExchangeName = (type) => {
    switch ((type || "").toLowerCase()) {
      case "nse":
        return "NSE-FUTURE";
      case "mcx":
        return "MCX-FUTURE";
      case "fo":
        return "NSE-OPTIONS";
      default:
        return type || "-";
    }
  };

  return (
    <div
      style={{
        width: windowSize[0] - 100,
        height: windowSize[1] - 170,
      }}
    >
      {/* FILTER */}
      <Filter
        search={search}
        onFilter={handleFilter}
        records={trade.length}
        userType={userType}
        handleBulkDelete={handleBulkDelete}
        selectedTrades={selectedTrades}
        user_id={user_id}
        downloadExcel={downloadExcel}
        downloadUserPDF={downloadUserPDF}
      />

      {/* TOTAL CARD */}
      <TotalCard
        commission={commission}
        booked={booked}
        rent={rent}
        nseCash={nseCash}
        userType={userType}
        trade={trade}
      />

      {/* MENU */}
      <Menu
        userType={userType}
        trade_delete={tradeDelete}
        trade_search={tradeSearch}
        handlewSort={handleSort}
      />

      {/* TRADE LIST */}
      <div
        style={{
          width: windowSize[0] - 100,
          height: windowSize[1] - 330,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Loading height={windowSize[1] - 330} />
        ) : trade.length > 0 ? (
          <div
            style={{
              width: windowSize[0] - 100,
              height: windowSize[1] - 330,
              overflowY: "auto",
            }}
          >
            {trade.map((item) => (
              <TradeCard
                key={item.id}
                item={item}
                reload={fetchTrade}
                trade_delete={tradeDelete}
                userType={userType}
                isSelected={selectedTrades.includes(item.id)}
                toggleSelect={toggleSelect}
              />
            ))}
          </div>
        ) : isFetchedOnce ? (
          <NotFound
            height={windowSize[1] - 330}
            title="Nothing here"
            subtext="No Trade Found."
          />
        ) : null}
      </div>
    </div>
  );
}
